import React, { useState, useMemo, useEffect } from 'react';
import {
  useGetFormateursQuery,
  useCreateFormateurMutation,
  useUpdateFormateurMutation,
  useDeleteFormateurMutation,
  useGetFormateurMatieresQuery,
  useAssignerMatieresMutation,
  useGetAttributionsAnneeQuery,
  Formateur,
  FormateurCreate,
} from '../../services/formateursApi';
import { useGetMatieresQuery } from '../../services/matieresApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import { getAnneeAcademique } from '../../services/baseApi';
import { useGetParcoursListQuery, useGetPolesQuery, useGetOptionsQuery } from '../../services/polesOptionsApi';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Save,
  X,
  User,
  CheckCircle,
  AlertCircle,
  BookOpen,
  Filter,
  ChevronDown,
  ChevronRight,
  Mail,
  Phone,
  Award,
  Eye,
  Printer,
} from 'lucide-react';

interface FormateurForm {
  nom: string;
  prenoms: string;
  email: string;
  telephone: string;
  specialite: string;
  actif: boolean;
}

const emptyForm: FormateurForm = {
  nom: '',
  prenoms: '',
  email: '',
  telephone: '',
  specialite: '',
  actif: true,
};

export default function GestionFormateurs() {
  // ─── State ───
  const [searchFormateur, setSearchFormateur] = useState('');
  const [filterActif, setFilterActif] = useState<'all' | 'actif' | 'inactif'>('actif');
  const [selectedFormateur, setSelectedFormateur] = useState<Formateur | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingFormateur, setEditingFormateur] = useState<Formateur | null>(null);
  const [form, setForm] = useState<FormateurForm>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<Formateur | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Filtres matières
  const [searchMatiere, setSearchMatiere] = useState('');
  const [filterNiveau, setFilterNiveau] = useState('');
  const [filterParcours, setFilterParcours] = useState('');
  const [filterPole, setFilterPole] = useState('');
  const [expandedClasses, setExpandedClasses] = useState<Set<string>>(new Set());
  const [matiereTab, setMatiereTab] = useState<'attribuees' | 'toutes'>('attribuees');

  // Matières cochées (IDs)
  const [checkedMatieres, setCheckedMatieres] = useState<Set<number>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  // Modal aperçu matières formateur
  const [previewFormateur, setPreviewFormateur] = useState<Formateur | null>(null);

  // Rapport imprimable « Formateurs par pôle / niveau / semestre + module »
  const [rapPole, setRapPole] = useState('');
  const [rapNiveau, setRapNiveau] = useState('');
  const [rapSemestre, setRapSemestre] = useState('');

  // ─── Queries ───
  const { data: formateurs, isLoading: loadingFormateurs } = useGetFormateursQuery(
    filterActif === 'all' ? undefined : { actif: filterActif === 'actif' }
  );
  const { data: anneeActive } = useGetAnneeActiveQuery();
  // Année de travail = année SÉLECTIONNÉE dans le header (localStorage →
  // schéma via X-Annee), et non l'année ACTIVE en base. Permet de consulter /
  // imprimer les formateurs d'une année antérieure (ex. 2025-2026). Le header
  // recharge la page au changement d'année, donc la lecture localStorage suffit.
  const anneeTravail = getAnneeAcademique() || anneeActive?.libelle || '';
  const { data: matieres } = useGetMatieresQuery();
  const { data: parcoursList } = useGetParcoursListQuery();
  const { data: polesList } = useGetPolesQuery();
  const { data: optionsList } = useGetOptionsQuery({ actif: true });

  const { data: formateurMatieres, refetch: refetchMatieres } = useGetFormateurMatieresQuery(
    { id: selectedFormateur?.id ?? 0, annee_academique: anneeTravail },
    { skip: !selectedFormateur || !anneeTravail }
  );

  const { data: previewMatieres } = useGetFormateurMatieresQuery(
    { id: previewFormateur?.id ?? 0, annee_academique: anneeTravail },
    { skip: !previewFormateur || !anneeTravail }
  );

  const { data: attributionsAnnee } = useGetAttributionsAnneeQuery(
    { annee_academique: anneeTravail },
    { skip: !anneeTravail }
  );

  // ─── Mutations ───
  const [createFormateur, { isLoading: creating }] = useCreateFormateurMutation();
  const [updateFormateur, { isLoading: updating }] = useUpdateFormateurMutation();
  const [deleteFormateur] = useDeleteFormateurMutation();
  const [assignerMatieres, { isLoading: assigning }] = useAssignerMatieresMutation();

  // ─── Effects ───
  // Initialiser les cases cochées quand les données du formateur changent
  useEffect(() => {
    if (formateurMatieres) {
      setCheckedMatieres(new Set(formateurMatieres.map((fm) => fm.matiere)));
      setHasChanges(false);
    }
  }, [formateurMatieres]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // ─── Filtrage formateurs ───
  const filteredFormateurs = useMemo(() => {
    if (!formateurs) return [];
    return formateurs.filter((f) => {
      const search = searchFormateur.toLowerCase();
      return (
        f.nom.toLowerCase().includes(search) ||
        f.prenoms.toLowerCase().includes(search) ||
        f.specialite?.toLowerCase().includes(search)
      );
    });
  }, [formateurs, searchFormateur]);

  // ─── Rapport imprimable : un formateur, ses modules + semestre, options en liste ───
  const imprimerRapportFormateurs = () => {
    const optPole: Record<string, string> = {};
    (optionsList || []).forEach((o: any) => { if (o?.nom) optPole[o.nom] = o.pole_nom || '—'; });
    const esc = (s: any) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // Agrégation : une ligne par (pôle, formateur, niveau, semestre, module) ; les
    // options concernées sont regroupées dans une seule case.
    type Agg = { pole: string; formateur: string; niveau: string; semestre: number; module: string; options: Set<string> };
    const map = new Map<string, Agg>();
    (matieres || []).forEach((m: any) => {
      const attr = attributionsAnnee?.[String(m.id)];
      if (!attr) return;
      const pole = optPole[m.option || ''] || '—';
      const niveau = m.niveau || '—';
      const semestre = Number(m.semestre ?? 0);
      if (rapPole && pole !== rapPole) return;
      if (rapNiveau && niveau !== rapNiveau) return;
      if (rapSemestre && String(semestre) !== rapSemestre) return;
      const formateur = attr.formateur_nom || '—';
      const key = [pole, formateur, niveau, semestre, m.nom_matiere].join('¦');
      let a = map.get(key);
      if (!a) { a = { pole, formateur, niveau, semestre, module: m.nom_matiere, options: new Set() }; map.set(key, a); }
      if (m.option) {
        // Code court de la classe (ex. L1CGE, L2GRH) au lieu du nom long de l'option.
        const optCode = optionNomToCode.get(m.option);
        a.options.add(optCode ? `${niveau !== '—' ? niveau : ''}${optCode}` : m.option);
      }
    });
    const aggs = Array.from(map.values());
    if (aggs.length === 0) {
      setNotification({ type: 'error', text: 'Aucune attribution ne correspond à ces filtres.' });
      return;
    }
    const NIV = ['L1', 'L2', 'L3', 'M1', 'M2'];
    const nivOrder = (n: string) => { const i = NIV.indexOf(n); return i < 0 ? 99 : i; };
    const poles = Array.from(new Set(aggs.map((a) => a.pole))).sort();
    let totalAttr = 0;
    aggs.forEach((a) => { totalAttr += a.options.size; });
    const nbFormateurs = new Set(aggs.map((a) => a.pole + '¦' + a.formateur)).size;

    const filtres = [rapPole && `Pôle : ${rapPole}`, rapNiveau && `Niveau : ${rapNiveau}`,
      rapSemestre && `Semestre : ${rapSemestre}`].filter(Boolean).join(' · ') || 'Tous';
    const annee = anneeTravail;

    let items = `<div class="item info">Année ${esc(annee)} &nbsp;·&nbsp; Filtre : ${esc(filtres)}`
      + ` &nbsp;·&nbsp; ${nbFormateurs} formateur(s) &nbsp;·&nbsp; ${aggs.length} module(s)/semestre &nbsp;·&nbsp; ${totalAttr} attribution(s)</div>`;
    poles.forEach((pole) => {
      const inPole = aggs.filter((a) => a.pole === pole);
      const formateurs = Array.from(new Set(inPole.map((a) => a.formateur))).sort((a, b) => a.localeCompare(b));
      items += `<div class="item pole">${esc(pole)}</div>`;
      formateurs.forEach((f) => {
        const list = inPole.filter((a) => a.formateur === f).sort(
          (x, y) => nivOrder(x.niveau) - nivOrder(y.niveau) || x.semestre - y.semestre || x.module.localeCompare(y.module));
        const body = list.map((a) => {
          const opts = Array.from(a.options).sort().join(', ');
          return `<tr><td class="niv">${esc(a.niveau)}</td><td class="sem">S${esc(a.semestre)}</td>`
            + `<td class="mod">${esc(a.module)}</td><td class="opt">${esc(opts)}</td></tr>`;
        }).join('');
        items += `<div class="item fblock"><div class="fname">${esc(f)}`
          + ` <span class="fcount">(${list.length} module${list.length > 1 ? 's' : ''})</span></div>`
          + '<table><thead><tr><th class="niv">Niveau</th><th class="sem">Sem.</th><th>Module</th>'
          + '<th>Classes concernées</th></tr></thead><tbody>' + body + '</tbody></table></div>';
      });
    });

    const headHtml = '<div class="inst">INSTITUT INTERNATIONAL POLYTECHNIQUE &amp; COMMERCE 2i</div>'
      + '<div class="ttl">Liste des formateurs et modules associés</div>';
    const footL = 'Institut 2i — Formateurs' + (annee ? ' · ' + annee : '');

    const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Formateurs et modules</title>
      <style>
        @page { size: A4 landscape; margin: 0; }
        * { box-sizing: border-box; }
        body { font-family: Arial, Helvetica, sans-serif; color: #1f2937; font-size: 10px; margin: 0; }
        #source { position: absolute; left: -9999px; top: 0; width: 273mm; }
        .page { width: 297mm; height: 210mm; display: flex; flex-direction: column; padding: 8mm 12mm; overflow: hidden; page-break-after: always; }
        .page:last-child { page-break-after: auto; }
        .phead { flex: 0 0 auto; text-align: center; border-bottom: 2px solid #1e3a8a; padding-bottom: 4px; margin-bottom: 6px; }
        .phead .inst { font-weight: 700; color: #1e3a8a; font-size: 12px; }
        .phead .ttl { font-weight: 700; font-size: 13px; margin-top: 1px; }
        .content { flex: 1 1 auto; overflow: hidden; }
        .pfoot { flex: 0 0 auto; border-top: 1px solid #d1d5db; margin-top: 6px; padding-top: 3px; display: flex; justify-content: space-between; color: #6b7280; font-size: 8px; }
        .info { font-size: 9px; color: #6b7280; margin-bottom: 6px; }
        .pole { background: #1e3a8a; color: #fff; padding: 3px 8px; font-size: 12px; font-weight: 700; border-radius: 3px; margin: 8px 0 4px; }
        .fblock { break-inside: avoid; margin: 0 0 7px; }
        .fname { font-weight: 700; font-size: 11px; color: #1e3a8a; margin: 0 0 2px; }
        .fname .fcount { color: #6b7280; font-weight: 400; font-size: 9px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #d1d5db; padding: 2px 6px; text-align: left; vertical-align: top; }
        th { background: #eff6ff; font-size: 9px; text-transform: uppercase; letter-spacing: .02em; }
        td.niv, th.niv { width: 50px; text-align: center; }
        td.sem, th.sem { width: 44px; text-align: center; }
        td.mod { font-weight: 600; width: 210px; }
        td.opt { color: #374151; }
      </style></head><body>
      <div id="tpl-head" style="display:none">${headHtml}</div>
      <div id="source">${items}</div>
      <div id="pages"></div>
      <script>
      (function () {
        var FOOTL = ${JSON.stringify(footL)};
        function paginate() {
          var src = document.getElementById('source');
          var pages = document.getElementById('pages');
          var head = document.getElementById('tpl-head').innerHTML;
          var items = [].slice.call(src.children);
          src.parentNode.removeChild(src);
          var pageNo = 0, content = null, avail = 0, used = 0;
          function newPage() {
            pageNo++;
            var p = document.createElement('div'); p.className = 'page';
            p.innerHTML = '<div class="phead">' + head + '</div><div class="content"></div>'
              + '<div class="pfoot"><span>' + FOOTL + '</span>'
              + '<span>Page <span class="pn">' + pageNo + '</span> / <span class="pt">?</span></span></div>';
            pages.appendChild(p);
            content = p.querySelector('.content');
            avail = content.clientHeight; used = 0;
          }
          newPage();
          for (var i = 0; i < items.length; i++) {
            var it = items[i];
            content.appendChild(it);
            var h = it.offsetHeight;
            if (used + h > avail - 6 && content.children.length > 1) {
              content.removeChild(it);
              newPage();
              content.appendChild(it);
              h = it.offsetHeight;
            }
            used += h;
          }
          var tot = pageNo;
          [].slice.call(document.querySelectorAll('.pt')).forEach(function (e) { e.textContent = tot; });
        }
        window.addEventListener('load', function () {
          paginate();
          setTimeout(function () { try { window.focus(); window.print(); } catch (e) {} }, 300);
        });
      })();
      </script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { setNotification({ type: 'error', text: "Le navigateur a bloqué l'ouverture. Autorisez les popups." }); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  // ─── Filtrage matières ───
  // Maps pour le filtrage par parcours/pôle via les options
  const optionsByParcours = useMemo(() => {
    const map = new Map<number, Set<string>>();
    optionsList?.forEach((opt) => {
      if (opt.parcours) {
        const pid = typeof opt.parcours === 'number' ? opt.parcours : 0;
        if (!map.has(pid)) map.set(pid, new Set());
        map.get(pid)!.add(opt.nom);
      }
    });
    return map;
  }, [optionsList]);

  const optionsByPole = useMemo(() => {
    const map = new Map<number, Set<string>>();
    optionsList?.forEach((opt) => {
      if (opt.pole) {
        if (!map.has(opt.pole)) map.set(opt.pole, new Set());
        map.get(opt.pole)!.add(opt.nom);
      }
    });
    return map;
  }, [optionsList]);

  // Map option nom → code pour construire le code classe (ex: "Maintenance Industrielle" → "MI")
  const optionNomToCode = useMemo(() => {
    const map = new Map<string, string>();
    optionsList?.forEach((opt) => {
      if (opt.nom && opt.code) {
        map.set(opt.nom, opt.code);
      }
    });
    return map;
  }, [optionsList]);

  const filteredMatieres = useMemo(() => {
    if (!matieres) return [];

    return matieres.filter((m) => {
      // Recherche texte
      if (searchMatiere) {
        const s = searchMatiere.toLowerCase();
        if (
          !m.code_matiere?.toLowerCase().includes(s) &&
          !m.nom_matiere?.toLowerCase().includes(s)
        )
          return false;
      }

      // Filtre niveau
      if (filterNiveau && m.niveau !== filterNiveau) return false;

      // Filtre parcours : la matière doit appartenir à une option de ce parcours
      if (filterParcours) {
        const parcoursId = parseInt(filterParcours);
        const optNames = optionsByParcours.get(parcoursId);
        if (!optNames || !m.option || !optNames.has(m.option)) return false;
      }

      // Filtre pôle : la matière doit appartenir à une option de ce pôle
      if (filterPole) {
        const poleId = parseInt(filterPole);
        const optNames = optionsByPole.get(poleId);
        if (!optNames || !m.option || !optNames.has(m.option)) return false;
      }

      return true;
    });
  }, [matieres, searchMatiere, filterNiveau, filterParcours, filterPole, optionsByParcours, optionsByPole]);

  // Grouper par classe (niveau + option code)
  const matieresParClasse = useMemo(() => {
    const source = matiereTab === 'attribuees'
      ? filteredMatieres.filter((m) => checkedMatieres.has(m.id))
      : filteredMatieres;

    const groups: Record<string, { niveau: string; classeCode: string; matieres: typeof filteredMatieres }> = {};
    source.forEach((m) => {
      const optCode = m.option ? optionNomToCode.get(m.option) : null;
      const classeCode = optCode ? `${m.niveau || ''}${optCode}` : m.niveau || 'Autre';
      const niveau = m.niveau || 'Autre';
      if (!groups[classeCode]) groups[classeCode] = { niveau, classeCode, matieres: [] };
      groups[classeCode].matieres.push(m);
    });
    // Trier les matières dans chaque groupe par nom
    Object.values(groups).forEach((g) =>
      g.matieres.sort((a, b) => a.nom_matiere.localeCompare(b.nom_matiere))
    );
    return groups;
  }, [filteredMatieres, checkedMatieres, matiereTab, optionNomToCode]);

  // Trier les classes par niveau puis code
  const niveauxOrdonnes = ['L1', 'L2', 'L3', 'M1', 'M2'];
  const classesOrdonnees = useMemo(() => {
    return Object.keys(matieresParClasse).sort((a, b) => {
      const nivA = matieresParClasse[a].niveau;
      const nivB = matieresParClasse[b].niveau;
      const idxA = niveauxOrdonnes.indexOf(nivA);
      const idxB = niveauxOrdonnes.indexOf(nivB);
      if (idxA !== idxB) return idxA - idxB;
      return a.localeCompare(b);
    });
  }, [matieresParClasse]);

  // ─── Handlers formateur CRUD ───
  const handleOpenCreate = () => {
    setEditingFormateur(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const handleOpenEdit = (f: Formateur) => {
    setEditingFormateur(f);
    setForm({
      nom: f.nom,
      prenoms: f.prenoms,
      email: f.email,
      telephone: f.telephone,
      specialite: f.specialite,
      actif: f.actif,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.nom.trim()) {
      setNotification({ type: 'error', text: 'Le nom est requis' });
      return;
    }
    try {
      if (editingFormateur) {
        const result = await updateFormateur({ id: editingFormateur.id, data: form }).unwrap();
        setNotification({ type: 'success', text: `${result.nom_complet} modifié avec succès` });
        if (selectedFormateur?.id === editingFormateur.id) {
          setSelectedFormateur(result);
        }
      } else {
        const result = await createFormateur(form).unwrap();
        setNotification({ type: 'success', text: `${result.nom_complet} ajouté avec succès` });
        setSelectedFormateur(result);
      }
      setShowModal(false);
    } catch (err: any) {
      setNotification({ type: 'error', text: err.data?.detail || 'Erreur lors de la sauvegarde' });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteFormateur(deleteConfirm.id).unwrap();
      setNotification({ type: 'success', text: `${deleteConfirm.nom} supprimé` });
      if (selectedFormateur?.id === deleteConfirm.id) {
        setSelectedFormateur(null);
      }
      setDeleteConfirm(null);
    } catch (err: any) {
      setNotification({ type: 'error', text: err.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  // ─── Handlers matières ───
  const handleToggleMatiere = (matiereId: number) => {
    setCheckedMatieres((prev) => {
      const next = new Set(prev);
      if (next.has(matiereId)) next.delete(matiereId);
      else next.add(matiereId);
      return next;
    });
    setHasChanges(true);
  };

  const handleToggleClasse = (classeCode: string) => {
    setExpandedClasses((prev) => {
      const next = new Set(prev);
      if (next.has(classeCode)) next.delete(classeCode);
      else next.add(classeCode);
      return next;
    });
  };

  const handleSaveAttributions = async () => {
    if (!selectedFormateur || !anneeTravail) return;
    try {
      const result = await assignerMatieres({
        id: selectedFormateur.id,
        data: {
          matiere_ids: Array.from(checkedMatieres),
          annee_academique: anneeTravail,
        },
      }).unwrap();

      if (result.conflits && result.conflits.length > 0) {
        const conflitMsg = result.conflits
          .map((c) => `• ${c.matiere_nom} → ${c.formateur_nom}`)
          .join('\n');
        setNotification({
          type: 'error',
          text: `${result.conflits.length} matière(s) déjà attribuée(s) :\n${conflitMsg}`,
        });
        // Décocher les matières en conflit
        setCheckedMatieres((prev) => {
          const next = new Set(prev);
          result.conflits!.forEach((c) => next.delete(c.matiere_id));
          return next;
        });
      } else {
        setNotification({
          type: 'success',
          text: `Attributions mises à jour : ${result.ajoutees} ajoutée(s), ${result.retirees} retirée(s). Total : ${result.total}`,
        });
      }
      setHasChanges(false);
      refetchMatieres();
    } catch (err: any) {
      setNotification({ type: 'error', text: err.data?.detail || 'Erreur lors de la sauvegarde' });
    }
  };

  // Nombre de matières cochées par classe (pour le badge)
  const nbCheckedParClasse = useMemo(() => {
    const counts: Record<string, number> = {};
    if (!matieres) return counts;
    matieres.forEach((m) => {
      if (checkedMatieres.has(m.id)) {
        const optCode = m.option ? optionNomToCode.get(m.option) : null;
        const classeCode = optCode ? `${m.niveau || ''}${optCode}` : m.niveau || 'Autre';
        counts[classeCode] = (counts[classeCode] || 0) + 1;
      }
    });
    return counts;
  }, [matieres, checkedMatieres, optionNomToCode]);

  // ─── Render ───
  return (
    <div className="p-6 max-w-full">
      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-2 max-w-md ${
            notification.type === 'success'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
          }`}
        >
          {notification.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span className="text-sm">{notification.text}</span>
          <button onClick={() => setNotification(null)} className="ml-2">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <User size={28} />
          Gestion des Formateurs
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Gérer les formateurs et leurs attributions de matières
          {anneeTravail && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
              {anneeTravail}
            </span>
          )}
        </p>
      </div>

      {/* Barre d'impression : liste des formateurs par pôle / niveau / semestre */}
      <div className="mb-5 p-4 bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mr-1">
          <Printer size={16} /> Imprimer la liste des formateurs
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Pôle</label>
          <select
            value={rapPole}
            onChange={(e) => setRapPole(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[180px]"
          >
            <option value="">Tous les pôles</option>
            {Array.from(new Set((optionsList || []).map((o: any) => o.pole_nom).filter(Boolean)))
              .sort()
              .map((p) => (
                <option key={p as string} value={p as string}>{p as string}</option>
              ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Niveau</label>
          <select
            value={rapNiveau}
            onChange={(e) => setRapNiveau(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">Tous</option>
            {['L1', 'L2', 'L3', 'M1', 'M2'].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Semestre</label>
          <select
            value={rapSemestre}
            onChange={(e) => setRapSemestre(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">Tous</option>
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map((s) => (
              <option key={s} value={s}>S{s}</option>
            ))}
          </select>
        </div>
        <button
          onClick={imprimerRapportFormateurs}
          className="ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Printer size={16} /> Imprimer
        </button>
      </div>

      <div className="flex gap-6 h-[calc(100vh-180px)]">
        {/* ═══ COLONNE GAUCHE : Liste des formateurs ═══ */}
        <div className="w-80 flex-shrink-0 flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700">
          {/* Recherche + filtre */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un formateur..."
                value={searchFormateur}
                onChange={(e) => setSearchFormateur(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-1">
              {(['actif', 'inactif', 'all'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilterActif(f)}
                  className={`flex-1 px-2 py-1 text-xs rounded-md font-medium transition-colors ${
                    filterActif === f
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {f === 'all' ? 'Tous' : f === 'actif' ? 'Actifs' : 'Inactifs'}
                </button>
              ))}
            </div>
            <button
              onClick={handleOpenCreate}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Plus size={16} /> Ajouter un formateur
            </button>
          </div>

          {/* Liste */}
          <div className="flex-1 overflow-y-auto">
            {loadingFormateurs ? (
              <div className="p-4 text-center text-gray-500">Chargement...</div>
            ) : filteredFormateurs.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">Aucun formateur trouvé</div>
            ) : (
              filteredFormateurs.map((f) => (
                <div
                  key={f.id}
                  onClick={() => setSelectedFormateur(f)}
                  className={`px-4 py-3 cursor-pointer border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                    selectedFormateur?.id === f.id
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
                      : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewFormateur(f);
                        }}
                        className="font-medium text-sm text-gray-900 dark:text-white truncate hover:text-blue-600 dark:hover:text-blue-400 hover:underline text-left"
                        title="Voir les matières attribuées"
                      >
                        {f.nom} {f.prenoms}
                      </button>
                      {f.specialite && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{f.specialite}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      {!f.actif && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded">
                          Inactif
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEdit(f);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                        title="Modifier"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(f);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Supprimer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer stats */}
          <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
            {filteredFormateurs.length} formateur{filteredFormateurs.length > 1 ? 's' : ''}
          </div>
        </div>

        {/* ═══ COLONNE DROITE : Détails + Attribution matières ═══ */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
          {!selectedFormateur ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500">
              <div className="text-center">
                <User size={48} className="mx-auto mb-3 opacity-50" />
                <p>Sélectionnez un formateur dans la liste</p>
                <p className="text-sm mt-1">ou ajoutez-en un nouveau</p>
              </div>
            </div>
          ) : (
            <>
              {/* Info formateur */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                      {selectedFormateur.nom} {selectedFormateur.prenoms}
                    </h2>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {selectedFormateur.specialite && (
                        <span className="flex items-center gap-1">
                          <Award size={13} /> {selectedFormateur.specialite}
                        </span>
                      )}
                      {selectedFormateur.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={13} /> {selectedFormateur.email}
                        </span>
                      )}
                      {selectedFormateur.telephone && (
                        <span className="flex items-center gap-1">
                          <Phone size={13} /> {selectedFormateur.telephone}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {checkedMatieres.size} matière{checkedMatieres.size > 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={() => handleOpenEdit(selectedFormateur)}
                      className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <Edit2 size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Onglets Attribuées / Toutes + filtres */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
                {/* Tabs */}
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700/50 rounded-lg p-0.5">
                  <button
                    onClick={() => setMatiereTab('attribuees')}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      matiereTab === 'attribuees'
                        ? 'bg-white dark:bg-gray-600 text-blue-700 dark:text-blue-300 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                    }`}
                  >
                    Attribuées
                    {checkedMatieres.size > 0 && (
                      <span className="ml-1.5 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-[10px] font-semibold">
                        {checkedMatieres.size}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setMatiereTab('toutes')}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      matiereTab === 'toutes'
                        ? 'bg-white dark:bg-gray-600 text-blue-700 dark:text-blue-300 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                    }`}
                  >
                    Toutes les matières
                  </button>
                </div>

                {/* Filtres */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                  <div className="relative lg:col-span-1">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Chercher matière..."
                      value={searchMatiere}
                      onChange={(e) => setSearchMatiere(e.target.value)}
                      className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <select
                    value={filterNiveau}
                    onChange={(e) => setFilterNiveau(e.target.value)}
                    className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Tous niveaux</option>
                    <option value="L1">L1</option>
                    <option value="L2">L2</option>
                    <option value="L3">L3</option>
                    <option value="M1">M1</option>
                    <option value="M2">M2</option>
                  </select>
                  <select
                    value={filterParcours}
                    onChange={(e) => setFilterParcours(e.target.value)}
                    className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Tous parcours</option>
                    {parcoursList?.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nom}
                      </option>
                    ))}
                  </select>
                  <select
                    value={filterPole}
                    onChange={(e) => setFilterPole(e.target.value)}
                    className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Tous pôles</option>
                    {polesList?.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nom}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Liste des matières groupées par classe */}
              <div className="flex-1 overflow-y-auto p-4">
                {classesOrdonnees.length === 0 ? (
                  <div className="text-center text-gray-400 dark:text-gray-500 py-8">
                    {matiereTab === 'attribuees'
                      ? 'Aucune matière attribuée à ce formateur'
                      : 'Aucune matière trouvée avec ces filtres'}
                  </div>
                ) : (
                  classesOrdonnees.map((classeCode) => {
                    const group = matieresParClasse[classeCode];
                    const isExpanded = expandedClasses.has(classeCode);
                    const nbChecked = nbCheckedParClasse[classeCode] || 0;

                    return (
                      <div key={classeCode} className="mb-2">
                        {/* Header classe */}
                        <button
                          onClick={() => handleToggleClasse(classeCode)}
                          className="w-full flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700/50 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        >
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-bold">
                            {classeCode}
                          </span>
                          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                            {group.matieres.length} matière{group.matieres.length > 1 ? 's' : ''}
                          </span>
                          {nbChecked > 0 && matiereTab === 'toutes' && (
                            <span className="ml-auto px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full font-medium">
                              {nbChecked} attribuée{nbChecked > 1 ? 's' : ''}
                            </span>
                          )}
                        </button>

                        {/* Matières de la classe */}
                        {isExpanded && (
                          <div className="mt-1 space-y-0.5 ml-2">
                            {group.matieres.map((m) => {
                              const isChecked = checkedMatieres.has(m.id);
                              const attr = attributionsAnnee?.[String(m.id)];
                              const attribueeAutre = attr && attr.formateur_id !== selectedFormateur?.id;

                              return (
                                <label
                                  key={m.id}
                                  className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                    isChecked
                                      ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                                      : attribueeAutre
                                        ? 'bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/30'
                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 border border-transparent'
                                  }`}
                                  title={attribueeAutre ? `Déjà attribuée à ${attr.formateur_nom}` : undefined}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleToggleMatiere(m.id)}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm text-gray-800 dark:text-gray-200">
                                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400 mr-1.5">
                                        {m.code_matiere}
                                      </span>
                                      {m.nom_matiere}
                                    </span>
                                    {attribueeAutre && (
                                      <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 truncate">
                                        Attribuée à {attr.formateur_nom}
                                      </p>
                                    )}
                                  </div>
                                  {m.semestre && (
                                    <span className="flex-shrink-0 text-[10px] text-gray-400 dark:text-gray-500">
                                      S{m.semestre}
                                    </span>
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Bouton enregistrer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <button
                  onClick={handleSaveAttributions}
                  disabled={!hasChanges || assigning}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    hasChanges
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Save size={16} />
                  {assigning ? 'Enregistrement...' : 'Enregistrer les attributions'}
                  {hasChanges && (
                    <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                      {checkedMatieres.size} matière{checkedMatieres.size > 1 ? 's' : ''}
                    </span>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ═══ MODAL : Ajout / Modification formateur ═══ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingFormateur ? 'Modifier le formateur' : 'Nouveau formateur'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Nom de famille"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prénoms</label>
                <input
                  type="text"
                  value={form.prenoms}
                  onChange={(e) => setForm({ ...form, prenoms: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Prénoms"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="email@exemple.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone</label>
                  <input
                    type="text"
                    value={form.telephone}
                    onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="06 XX XX XX XX"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Spécialité</label>
                <input
                  type="text"
                  value={form.specialite}
                  onChange={(e) => setForm({ ...form, specialite: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Informatique, Mathématiques..."
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="actif"
                  checked={form.actif}
                  onChange={(e) => setForm({ ...form, actif: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="actif" className="text-sm text-gray-700 dark:text-gray-300">
                  Formateur actif
                </label>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={creating || updating}
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2"
              >
                <Save size={14} />
                {creating || updating ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL : Confirmation suppression ═══ */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 size={24} className="text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Supprimer le formateur ?</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <strong>{deleteConfirm.nom} {deleteConfirm.prenoms}</strong>
                <br />
                Cette action est irréversible. Toutes ses attributions de matières seront également supprimées.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL : Aperçu matières du formateur ═══ */}
      {previewFormateur && (() => {
        // Grouper les matières attribuées par classe
        const matieresAttribuees = previewMatieres || [];
        const grouped: Record<string, { classeCode: string; niveau: string; matieres: typeof matieresAttribuees }> = {};
        matieresAttribuees.forEach((fm) => {
          const optCode = fm.matiere_option ? optionNomToCode.get(fm.matiere_option) : null;
          const classeCode = optCode ? `${fm.matiere_niveau || ''}${optCode}` : fm.matiere_niveau || 'Autre';
          if (!grouped[classeCode]) grouped[classeCode] = { classeCode, niveau: fm.matiere_niveau || '', matieres: [] };
          grouped[classeCode].matieres.push(fm);
        });
        const orderedClasses = Object.keys(grouped).sort((a, b) => {
          const nA = niveauxOrdonnes.indexOf(grouped[a].niveau);
          const nB = niveauxOrdonnes.indexOf(grouped[b].niveau);
          if (nA !== nB) return nA - nB;
          return a.localeCompare(b);
        });

        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Eye size={20} className="text-blue-500" />
                    {previewFormateur.nom} {previewFormateur.prenoms}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {previewFormateur.specialite && <span>{previewFormateur.specialite} · </span>}
                    {matieresAttribuees.length} matière{matieresAttribuees.length > 1 ? 's' : ''} attribuée{matieresAttribuees.length > 1 ? 's' : ''}
                    {anneeTravail && <span> · {anneeTravail}</span>}
                  </p>
                </div>
                <button onClick={() => setPreviewFormateur(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <X size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {matieresAttribuees.length === 0 ? (
                  <div className="text-center text-gray-400 dark:text-gray-500 py-10">
                    <BookOpen size={40} className="mx-auto mb-3 opacity-40" />
                    <p className="text-sm">Aucune matière attribuée</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orderedClasses.map((classeCode) => {
                      const group = grouped[classeCode];
                      return (
                        <div key={classeCode}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-bold">
                              {classeCode}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {group.matieres.length} matière{group.matieres.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="ml-2 space-y-1">
                            {group.matieres.map((fm) => (
                              <div
                                key={fm.id}
                                className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-700/30 rounded-md"
                              >
                                <span className="font-mono text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                  {fm.matiere_code}
                                </span>
                                <span className="text-sm text-gray-800 dark:text-gray-200 flex-1 min-w-0 truncate">
                                  {fm.matiere_nom}
                                </span>
                                {fm.matiere_semestre && (
                                  <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                                    S{fm.matiere_semestre}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => {
                    setSelectedFormateur(previewFormateur);
                    setPreviewFormateur(null);
                  }}
                  className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2"
                >
                  <Edit2 size={14} />
                  Modifier les attributions
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
